import { useEffect, useMemo, useState } from "react"
import { useLocation, useParams } from "wouter"
import { useProjetoContext } from "@/context/projeto"
import { ICreateContract, IUpdateContract } from "@/interfaces/contracts.interface"
import {
  MembroEquipe,
  UseContratoFormProps,
} from "../contrato.types"
import {
  useGetContractById,
  useCreateContract,
  useUpdateContract,
  useUploadContractDocument,
} from "../queries/contrato-uses"
import { initFormContrato, mapContratoFormToPayload } from "../helpers/contrato-helpers"
import { ContractFormData } from "../components/form-contrato/schemas"
import { useGetContractsByMovie } from "@/services/queries/useContratos"

export function useContratoForm({ mode }: UseContratoFormProps) {
  const [, navigate] = useLocation()
  const params = useParams()
  const contractId = mode === "edit" ? Number(params.id) : undefined

  const { projetoSelecionado } = useProjetoContext()
  const movieId = projetoSelecionado?.id || 1

  // Queries
  const { data: contracts } = useGetContractsByMovie(movieId)
  const { data: contract, isLoading: isLoadingContract } = useGetContractById(
    contractId ?? 0
  )
  const { mutateAsync: uploadDocument } = useUploadContractDocument()
  const { mutateAsync: createContract, isPending: isCreating } = useCreateContract()
  const { mutateAsync: updateContract, isPending: isUpdating } = useUpdateContract()

  const equipe: MembroEquipe[] = useMemo(() => {
    if (!contracts?.data) return []
    const seen = new Set<number>()
    return contracts.data.reduce<MembroEquipe[]>((acc, c) => {
      const id = c.staffUser?.person?.id ?? 0
      if (seen.has(id)) return acc
      seen.add(id)
      acc.push({
        id,
        name: c.staffUser?.person?.name ?? "Sem nome",
        email: c.staffUser?.login ?? "Sem email",
        document: c.staffUser?.person?.document ?? "Sem documento",
        role: c.staffUser?.person?.role ?? "Sem função",
        pjName: c.staffUser?.person?.pjName ?? "Sem razão social",
        pjDocument: c.staffUser?.person?.pjDocument ?? "Sem documento",
      })
      return acc
    }, [])
  }, [contracts])

  const isPending = isCreating || isUpdating

  // Estado local
  const [submitAction, setSubmitAction] = useState<"save" | "send" | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)

  const form = initFormContrato(movieId)

  // Popula o formulário no modo edição
  useEffect(() => {
    if (mode !== "edit" || !contract?.data) return

    const c = contract.data

    form.reset({
      movieId: c.movieId,
      personId: c.staffUser?.person ? String(c.staffUser.person.id ?? "") : "",
      email: c.staffUser?.login ?? "",
      name: c.staffUser?.person?.name ?? "",
      document: c.staffUser?.person?.document ?? "",
      role: c.staffUser?.person?.role ?? "",
      total_amount: c.totalAmount?.toString() ?? "",
      month_amount: c.monthAmount?.toString() ?? "",
      contract_description: c.contractDescription ?? "",
      contract_till: c.contractTill ?? "",
      active: c.active ?? true,
      has_copyright: c.hasCopyright ? "true" : "false",
      uploaded_contract_url: c.uploadedContractUrl ?? "",
    })

    // Garante que Radix Select reflita o valor após reset
    form.setValue("has_copyright", c.hasCopyright ? "true" : "false")

    if (c.uploadedContractUrl) {
      setPreviewUrl(c.uploadedContractUrl)
    }
  }, [contract, mode, form])

  const handleFileUpload = async (file: File | null) => {
    if (!file) return
    try {
      const formData = new FormData()
      formData.append("file", file)
      const response = await uploadDocument(formData)
      setPreviewUrl(response.view_url)
      form.setValue("uploaded_contract_url", response.view_url)
    } catch (error) {
      console.error("Erro ao fazer upload do documento:", error)
    }
  }

  const handleMemberSelect = (member: MembroEquipe) => {
    form.setValue("email", member?.email ?? "", { shouldValidate: true })
    form.setValue("name", member?.name ?? "", { shouldValidate: true })
    form.setValue("document", member?.document ?? "", { shouldValidate: true })
    form.setValue("role", member?.role ?? "")
  }

  const handleMemberChange = (memberId: string) => {
    const member = equipe?.find((p) => p.id === Number(memberId))
    if (member) handleMemberSelect(member)
  }

  const handleClosePreview = () => {
    setPreviewUrl(null)
  }

  const handleActionSubmit = async (action: "save" | "send") => {
    setSubmitAction(action)
    await onSubmit(form.getValues())
    setSubmitAction(null)
  }

  const onSubmit = async (data: ContractFormData) => {
    try {
      const payload = mapContratoFormToPayload(
        data,
        movieId,
        contractId ? { id: contractId } : undefined
      )

      if (mode === "edit") {
        await updateContract(payload as IUpdateContract)
      } else {
        await createContract(payload as ICreateContract)
      }

      navigate("/contratual/contratos")
    } catch (error) {
      console.error("Erro ao salvar contrato:", error)
      // Erros de rede/API são tratados pelo onError das mutations (toast)
    }
  }

  return {
    form,
    equipe,
    isPending,
    isLoadingContract: mode === "edit" && isLoadingContract,
    previewUrl,
    submitAction,
    handleActionSubmit,
    handleFileUpload,
    handleMemberChange,
    handleClosePreview,
    onSubmit,
    navigate,
    mode,
  }
}
